import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { sortedByTier } from '../../shared/reorder-plan'
import {
  formFromSection,
  perSlugRows,
  reviewLadder,
  sectionErrorText,
  seedPerSlug,
  setAllPerSlug,
  tierRuleFromForm,
  toUpsertInput,
  validateForm,
  withPerSlug,
} from '../../shared/section-form'
import type {
  LadderKind,
  SectionFieldErrors,
  SectionForm,
} from '../../shared/section-form'
import type {
  BoOption,
  BoSection,
  SectionKind,
  UpsertSectionInput,
} from '../../shared/types'
import AdvancedFields from './AdvancedFields'
import SwatchEditor from './SwatchEditor'
import TierLadderEditor from './TierLadderEditor'

export interface SectionEditorDrawerProps {
  tierCount?: number
  open: boolean
  setId: string
  section?: BoSection
  options: BoOption[]
  saving?: boolean
  serverErrors?: SectionFieldErrors
  onClose: () => void
  onSave: (input: UpsertSectionInput) => void
}

export default function SectionEditorDrawer({
  tierCount,
  open,
  setId,
  section,
  options,
  saving = false,
  serverErrors,
  onClose,
  onSave,
}: SectionEditorDrawerProps) {
  const { formatMessage } = useIntl()
  const [form, setForm] = useState<SectionForm>(() => formFromSection(section))
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (open) {
      setForm(formFromSection(section))
      setSubmitted(false)
    }
  }, [open, section])

  const creating = !section
  const patch = (next: Partial<SectionForm>) =>
    setForm((prev) => ({ ...prev, ...next }))

  const localErrors = useMemo(() => validateForm(form), [form])
  const errors: SectionFieldErrors = {
    ...(submitted ? localErrors : {}),
    ...(serverErrors ?? {}),
  }
  const review = useMemo(
    () => reviewLadder(form, options, form.id),
    [form, options],
  )
  const slugRows = useMemo(
    () => perSlugRows(form, options, form.id),
    [form, options],
  )

  // Switching INTO per-option starts from what the current rule already implies. Starting from an
  // empty map made the screen say "N of N can never be picked" the moment the mode was chosen.
  const changeLadderKind = (ladderKind: LadderKind) =>
    patch(
      ladderKind === 'slugs'
        ? { ladderKind, perSlug: seedPerSlug(form, options, form.id) }
        : { ladderKind },
    )

  // A section still stored as a count ladder is converted only HERE, when a tier is actually
  // changed — and the whole implied map goes with it, not just the row that was touched.
  const changePerSlug = (key: string, tier: number) =>
    patch({
      ladderKind: 'slugs',
      perSlug: withPerSlug(seedPerSlug(form, options, form.id), key, tier),
    })

  const setAllPerSlugTo = (tier: number) =>
    patch({
      ladderKind: 'slugs',
      perSlug: setAllPerSlug(form, options, form.id, tier),
    })

  const submit = () => {
    setSubmitted(true)
    if (Object.keys(validateForm(form)).length > 0) {
      return
    }
    // Swatches re-sorted so tiers climb: a tier edit here would otherwise leave a T0 colour
    // buried mid-list. Grid reorders stay untouched — this runs only on an editor save.
    const swatches =
      form.kind === 'color' && form.ladderKind === 'slugs'
        ? sortedByTier(form.swatches, tierRuleFromForm(form))
        : form.swatches
    onSave(toUpsertInput(setId, { ...form, swatches }))
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}
    >
      <Stack sx={{ height: '100%' }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" component="h2">
            {formatMessage({
              id: creating
                ? 'avatarStudio.editor.titleCreate'
                : 'avatarStudio.editor.titleEdit',
            })}
          </Typography>
        </Box>
        <Divider />

        <Stack spacing={2.5} sx={{ p: 2, overflowY: 'auto', flexGrow: 1 }}>
          {errors.form && <Alert severity="error">{errors.form}</Alert>}

          <TextField
            size="small"
            label={formatMessage({ id: 'avatarStudio.editor.name' })}
            value={form.displayName}
            onChange={(e) => patch({ displayName: e.target.value })}
            error={Boolean(errors.displayName)}
            helperText={sectionErrorText(errors.displayName, (id) =>
              formatMessage({ id }),
            )}
            inputProps={{ maxLength: 120 }}
          />

          {creating ? (
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <TextField
                size="small"
                label={formatMessage({ id: 'avatarStudio.editor.id' })}
                value={form.id}
                onChange={(e) => patch({ id: e.target.value })}
                error={Boolean(errors.id)}
                helperText={
                  errors.id
                    ? formatMessage({ id: 'avatarStudio.editor.idInvalid' })
                    : undefined
                }
                inputProps={{ spellCheck: false }}
              />
              <TextField
                select={true}
                size="small"
                sx={{ minWidth: 150 }}
                label={formatMessage({ id: 'avatarStudio.editor.kind' })}
                value={form.kind}
                onChange={(e) => patch({ kind: e.target.value as SectionKind })}
              >
                <MenuItem value="option">
                  {formatMessage({ id: 'avatarStudio.section.kind.option' })}
                </MenuItem>
                <MenuItem value="color">
                  {formatMessage({ id: 'avatarStudio.section.kind.color' })}
                </MenuItem>
              </TextField>
            </Stack>
          ) : null}

          {creating && (
            <Typography variant="caption" color="text.secondary">
              {formatMessage({ id: 'avatarStudio.editor.idPermanent' })}{' '}
              {formatMessage({ id: 'avatarStudio.editor.kindAtCreation' })}
            </Typography>
          )}

          {creating ? null : (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary" translate="no">
                {form.id}
              </Typography>
              <Tooltip
                title={formatMessage({ id: 'avatarStudio.editor.kindLockedWhy' })}
              >
                <Chip
                  size="small"
                  tabIndex={0}
                  icon={<LockOutlinedIcon fontSize="small" />}
                  label={formatMessage({
                    id: `avatarStudio.section.kind.${form.kind}`,
                  })}
                />
              </Tooltip>
            </Stack>
          )}

          <Divider />

          {form.kind === 'color' && (
            <>
              <SwatchEditor
                swatches={form.swatches}
                error={errors.swatches}
                onChange={(swatches) => patch({ swatches })}
              />
              <Divider />
            </>
          )}

          <TierLadderEditor
            form={form}
            review={review}
            perSlugRows={slugRows}
            tierCount={tierCount}
            error={errors.perTier ?? errors.perSlug}
            onLadderKindChange={changeLadderKind}
            onPerSlugChange={changePerSlug}
            onSetAll={setAllPerSlugTo}
          />

          <Divider />
          <AdvancedFields form={form} errors={errors} onChange={patch} />
        </Stack>

        <Divider />
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={saving}>
            {formatMessage({ id: 'avatarStudio.editor.cancel' })}
          </Button>
          <Button variant="contained" onClick={submit} disabled={saving}>
            {formatMessage({
              id: saving
                ? 'avatarStudio.editor.saving'
                : 'avatarStudio.editor.save',
            })}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  )
}
